export const DETERMINISTIC_RANDOM_VERSION = 1;
export const DETERMINISTIC_RANDOM_ALGORITHM = 'mulberry32';
export const DEFAULT_RANDOM_SEED = 0x53455250;

const UINT32_RANGE = 0x100000000;

function assertUint32(value, name) {
  if (!Number.isInteger(value) || value < 0 || value >= UINT32_RANGE) {
    throw new RangeError(`${name} must be an unsigned 32-bit integer`);
  }
}

function assertCalls(value) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError('calls must be a non-negative integer');
  }
}

function hashStringSeed(value) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function normalizeRandomSeed(seed) {
  if (typeof seed === 'number' && Number.isFinite(seed)) {
    return Math.trunc(seed) >>> 0;
  }
  if (typeof seed === 'string' && seed.length > 0) {
    return hashStringSeed(seed);
  }
  throw new TypeError('seed must be a finite number or non-empty string');
}

export class DeterministicRandom {
  constructor({ seed = DEFAULT_RANDOM_SEED } = {}) {
    this.seed = normalizeRandomSeed(seed);
    this.state = this.seed;
    this.calls = 0;
  }

  setSeed(seed) {
    this.seed = normalizeRandomSeed(seed);
    this.state = this.seed;
    this.calls = 0;
    return this.snapshot();
  }

  reset() {
    this.state = this.seed;
    this.calls = 0;
    return this.snapshot();
  }

  restore(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') {
      throw new TypeError('random snapshot must be an object');
    }
    if (snapshot.version !== DETERMINISTIC_RANDOM_VERSION) {
      throw new RangeError(`Unsupported random snapshot version: ${snapshot.version}`);
    }
    if (snapshot.algorithm !== DETERMINISTIC_RANDOM_ALGORITHM) {
      throw new RangeError(`Unsupported random algorithm: ${snapshot.algorithm}`);
    }
    assertUint32(snapshot.seed, 'seed');
    assertUint32(snapshot.state, 'state');
    assertCalls(snapshot.calls);

    this.seed = snapshot.seed;
    this.state = snapshot.state;
    this.calls = snapshot.calls;
    return this.snapshot();
  }

  nextUint32() {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let value = this.state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    value = (value ^ (value >>> 14)) >>> 0;
    this.calls += 1;
    return value;
  }

  nextFloat() {
    return this.nextUint32() / UINT32_RANGE;
  }

  nextInt(min, maxExclusive) {
    if (!Number.isInteger(min) || !Number.isInteger(maxExclusive)) {
      throw new TypeError('min and maxExclusive must be integers');
    }
    const range = maxExclusive - min;
    if (range <= 0 || range > UINT32_RANGE) {
      throw new RangeError('maxExclusive must be greater than min with a range no larger than 2^32');
    }

    const rejectionLimit = Math.floor(UINT32_RANGE / range) * range;
    let value;
    do {
      value = this.nextUint32();
    } while (value >= rejectionLimit);
    return min + (value % range);
  }

  pick(values) {
    if (!Array.isArray(values) || values.length === 0) {
      throw new RangeError('values must be a non-empty array');
    }
    return values[this.nextInt(0, values.length)];
  }

  beginTick() {
    const tickSeed = this.nextUint32();
    return Object.freeze({
      ...this.snapshot(),
      tickSeed,
    });
  }

  snapshot() {
    return Object.freeze({
      version: DETERMINISTIC_RANDOM_VERSION,
      algorithm: DETERMINISTIC_RANDOM_ALGORITHM,
      seed: this.seed,
      state: this.state,
      calls: this.calls,
    });
  }
}
